import { useId, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { AuthHead } from '../components/AuthHead';

// Réinitialisation du mot de passe du compte de SECOURS local (bris de glace §24.9quinquies h) — deux
// temps sur une seule page : demander le lien (sans jeton), puis poser le mot de passe (avec jeton).
//
// ⚠️ POURQUOI CET ÉCRAN EXISTE : le serveur exposait déjà `/auth/password/{forgot,reset}` sans qu'aucune
// page ne les atteigne. Le lien envoyé par e-mail ne menait donc nulle part — un parcours à moitié câblé
// vaut un parcours absent (§23.0). Les comptes FÉDÉRÉS ne passent pas par ici : leur mot de passe vit
// à l'IdP, qui a sa propre réinitialisation.
export function Reset() {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const [mail, setMail] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);

  const label = { fontSize: 'var(--t-body)', fontWeight: 600, color: '#4a5158', display: 'block', marginBottom: 6 } as const;
  const input = { width: '100%', height: 44, padding: '0 13px', borderRadius: 'var(--r-sm)', border: '1px solid #e2e6ea', background: 'var(--paper)', color: '#131618', fontSize: 'var(--t-control)' } as const;

  // Demande du lien. ⚠️ La route est NON-ÉNUMÉRANTE (200 même si l'adresse est inconnue) : l'écran dit
  // donc « si un compte existe », jamais « e-mail envoyé » — l'affirmer serait inventer une certitude.
  const askLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try { await api.forgotPassword(mail.trim().toLowerCase()); setSent(true); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const apply = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (next.length < 12) { setErr('Le mot de passe doit faire au moins 12 caractères.'); return; }
    if (next !== confirm) { setErr('La confirmation ne correspond pas.'); return; }
    setBusy(true);
    try { await api.resetPassword(token, next); nav('/login', { replace: true }); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', background: 'linear-gradient(180deg, #effdf8, #f4f5f7)', padding: 20 }}>
      <form onSubmit={token ? apply : askLink} style={{ width: 400, maxWidth: '100%', background: 'var(--paper)', borderRadius: 'var(--r-lg)', padding: 30, boxShadow: '0 20px 50px -20px rgba(15,20,25,.3)' }}>
        <AuthHead title={token ? 'Nouveau mot de passe' : 'Mot de passe oublié'} />

        {err && <div style={{ background: '#fdeaea', color: '#b23', borderRadius: 'var(--r-sm)', padding: '10px 12px', fontSize: 'var(--t-lead)', marginBottom: 14, lineHeight: 1.45 }}>{err}</div>}

        {token ? (
          <>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor={`${fieldId}-0`} style={label}>Nouveau mot de passe</label>
              <input id={`${fieldId}-0`} style={input} type="password" value={next} onChange={(e) => { setNext(e.target.value); setErr(''); }} autoComplete="new-password" required autoFocus />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor={`${fieldId}-1`} style={label}>Confirmer</label>
              <input id={`${fieldId}-1`} style={input} type="password" value={confirm} onChange={(e) => { setConfirm(e.target.value); setErr(''); }} autoComplete="new-password" required />
            </div>
            <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 48, borderRadius: 'var(--r-sm)', fontSize: 'var(--t-h4)', cursor: busy ? 'wait' : undefined }}>
              {busy ? 'Enregistrement…' : 'Définir le mot de passe'}
            </button>
          </>
        ) : sent ? (
          <div style={{ background: '#effdf8', border: '1px solid #b9ead9', color: '#0f6b55', borderRadius: 'var(--r-sm)', padding: '13px 15px', fontSize: 'var(--t-body)', lineHeight: 1.5 }}>
            Demande enregistrée. <strong>Si un compte local existe pour cette adresse</strong>, un lien de
            réinitialisation vient de lui être envoyé — il expire rapidement. Rien n'est confirmé au-delà :
            cette page ne sait pas si l'adresse correspond à un compte.
          </div>
        ) : (
          <>
            <div style={{ background: '#fff8ec', border: '1px solid #f3dcae', color: '#8a6d3b', borderRadius: 'var(--r-sm)', padding: '11px 13px', fontSize: 'var(--t-body)', lineHeight: 1.45, marginBottom: 18 }}>
              Réservé au <strong>compte de secours local</strong>. Un accès Orgazone (SSO) se réinitialise
              depuis l'IdP, pas ici.
            </div>
            <div style={{ marginBottom: 18 }}>
              <label htmlFor={`${fieldId}-2`} style={label}>Adresse e-mail</label>
              <input id={`${fieldId}-2`} style={input} type="email" value={mail} onChange={(e) => { setMail(e.target.value); setErr(''); }} autoComplete="username" required autoFocus />
            </div>
            <button type="submit" disabled={busy} className="btn-cta" style={{ width: '100%', height: 48, borderRadius: 'var(--r-sm)', fontSize: 'var(--t-h4)', cursor: busy ? 'wait' : undefined }}>
              {busy ? 'Envoi…' : 'Recevoir un lien'}
            </button>
          </>
        )}

        <button type="button" onClick={() => nav('/login')} style={{ display: 'block', width: '100%', marginTop: 16, background: 'none', border: 'none', fontSize: 'var(--t-dense)', color: '#6c747c', cursor: 'pointer' }}>
          Retour à la connexion
        </button>
      </form>
    </div>
  );
}
